import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import { API_BASE, ApiError, localTokenStore, type Api } from './api';
import { initialLive, liveReducer, pollEvery } from './liveState';
import { isOpen } from './rideScreen';
import type { DriverLocation, Ride } from './types';

const FINISH_KEY = 'zr-finish';
const CLOSED_KEY = 'zr-closed';
/** A completed ride the rider hasn't closed yet (pay & rate). */
export const finishLater = {
  get: (): string | null => {
    try {
      return localStorage.getItem(FINISH_KEY);
    } catch {
      return null;
    }
  },
  set: (id: string | null) => {
    try {
      if (id && id === localStorage.getItem(CLOSED_KEY)) return; // already closed with Done
      if (id) localStorage.setItem(FINISH_KEY, id);
      else localStorage.removeItem(FINISH_KEY);
    } catch {
      /* ignore */
    }
  },
  /** Done on pay & rate: a late push or poll about this ride must not bring the screen back. */
  close: (id: string) => {
    try {
      localStorage.setItem(CLOSED_KEY, id);
      localStorage.removeItem(FINISH_KEY);
    } catch {
      /* ignore */
    }
  },
  isClosed: (id: string) => {
    try {
      return localStorage.getItem(CLOSED_KEY) === id;
    } catch {
      return false;
    }
  },
};

/**
 * The rider's live ride: REST is the source of truth, the socket makes it instant. Re-fetch on every (re)connect;
 * poll every 5 s while the socket is down and every 3 s while searching (matching progress isn't pushed). Both
 * feed one reducer (lib/liveState.ts) that never moves a ride backwards.
 */
export function useLiveRide(api: Api, enabled: boolean) {
  const [state, dispatch] = useReducer(liveReducer, initialLive);
  const [loaded, setLoaded] = useState(false);
  const [connected, setConnected] = useState(false);
  /** REST calls failing in a row (network down or the server unreachable). */
  const [failures, setFailures] = useState(0);
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));
  const rideRef = useRef<Ride | null>(null);
  rideRef.current = state.ride;
  const ride = state.ride;

  const refetch = useCallback(async () => {
    try {
      const { ride: cur } = await api.currentRide();
      if (cur && cur.status === 'COMPLETED' && finishLater.isClosed(cur.id)) {
        dispatch({ type: 'fetched', ride: null });
      } else if (cur) {
        if (cur.status === 'COMPLETED') finishLater.set(cur.id);
        dispatch({ type: 'fetched', ride: cur });
      } else {
        // nothing open: the ride we were showing may just have ended — read it once to show how it ended
        const prev = rideRef.current;
        const pending = finishLater.get();
        const id = prev?.id ?? pending;
        const r = id ? await api.ride(id).catch(() => null) : null;
        if (r && r.status === 'COMPLETED' && (pending === r.id || prev?.id === r.id)) {
          finishLater.set(r.id);
          dispatch({ type: 'fetched', ride: r });
        } else if (r && !isOpen(r.status) && prev?.id === r.id) dispatch({ type: 'fetched', ride: r });
        else dispatch({ type: 'fetched', ride: null });
      }
      setFailures(0);
    } catch (e) {
      // keep what we have; the next poll tries again
      if (e instanceof ApiError && (e.status === 0 || e.status >= 500)) setFailures((n) => n + 1);
    } finally {
      setLoaded(true);
    }
  }, [api]);

  useEffect(() => {
    const up = () => (setOnline(true), void refetch());
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, [refetch]);

  useEffect(() => {
    if (!enabled) return;
    void refetch();
    let socket: Socket | null = null;
    let stopped = false;
    const connect = () => {
      const token = localTokenStore.get()?.accessToken;
      if (!token || stopped) return;
      socket = io(API_BASE || window.location.origin, { auth: { token }, transports: ['websocket'], reconnection: true });
      socket.on('ready', () => {
        setConnected(true);
        void refetch();
      });
      socket.on('disconnect', () => setConnected(false));
      socket.on('connect_error', () => setConnected(false));
      socket.on('session:error', async () => {
        socket?.close();
        setConnected(false);
        if (await api.refresh().catch(() => false)) connect();
      });
      socket.on('session:ended', () => setConnected(false));
      socket.on('ride:updated', (r: Ride & { truncated?: boolean }) => {
        if (r.truncated || !r.pickup) return void refetch();
        if (r.status === 'COMPLETED' && finishLater.isClosed(r.id)) return;
        if (r.status === 'COMPLETED') finishLater.set(r.id);
        dispatch({ type: 'pushed', ride: r });
      });
      socket.on('ride:driver-location', (p: DriverLocation & { rideId: string }) => {
        dispatch({ type: 'location', rideId: p.rideId, at: { lat: p.lat, lng: p.lng, heading: p.heading, at: p.at } });
      });
    };
    connect();
    return () => {
      stopped = true;
      socket?.close();
      setConnected(false);
    };
  }, [enabled, api, refetch]);

  const every = enabled ? pollEvery(connected, ride) : null;
  useEffect(() => {
    if (every == null) return;
    const t = window.setInterval(() => void refetch(), every);
    return () => window.clearInterval(t);
  }, [every, refetch]);

  const setRide = useCallback((r: Ride | null) => dispatch({ type: 'local', ride: r }), []);

  return { ride, setRide, loaded, connected, refetch, offline: !online || failures >= 2 };
}
